import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  apiClient,
} from '@dukene/shared';
import {
  Search,
  Users as UsersIcon,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  ShoppingBag,
  UserCircle,
  UserPlus,
} from 'lucide-react';
import { format } from 'date-fns';

// User management page - all components use native HTML
type UserRole = 'CUSTOMER' | 'MERCHANT' | 'OWNER' | 'ADMIN' | 'AFFILIATE';
type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

interface AssignedStore {
  id: string;
  name: string;
  subdomain: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  plan?: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  store?: AssignedStore | null;
  _count?: {
    ownedStores: number;
  };
}

const statusIcons = {
  ACTIVE: CheckCircle,
  INACTIVE: AlertCircle,
  SUSPENDED: XCircle,
};

const roleIcons = {
  ADMIN: Shield,
  MERCHANT: ShoppingBag,
  OWNER: ShoppingBag,
  CUSTOMER: UserCircle,
  AFFILIATE: UserPlus,
};

export default function Users() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const queryClient = useQueryClient();

  // Fetch users
  const { data: users = [], error } = useQuery<User[]>({
    queryKey: ['users', roleFilter, statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (roleFilter !== 'ALL') params.append('role', roleFilter);
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);

      return apiClient.get<User[]>(`/users?${params}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: UserStatus }) => {
      return apiClient.patch(`/users/${userId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Extract users array safely from paginated response
  const userList = (users as any)?.users || (users as any)?.data || (Array.isArray(users) ? users : []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <p className="font-medium">Failed to load users</p>
      </div>
    );
  }

  const handleStatusToggle = (userId: string, currentStatus: UserStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    updateStatusMutation.mutate({ userId, status: newStatus });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight text-black">Users</h1>
        <p className="text-gray-500 mt-2">Manage platform users and their access</p>
      </div>

      {/* Stats Cards - Black & White */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <UsersIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Users</p>
            <p className="text-5xl font-black text-black mt-2">{userList.length}</p>
          </div>
        </div>

        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active</p>
            <p className="text-5xl font-black text-black mt-2">
              {userList.filter((u: any) => u.status === 'ACTIVE').length}
            </p>
          </div>
        </div>

        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Merchants</p>
            <p className="text-5xl font-black text-black mt-2">
              {userList.filter((u: any) => u.role === 'MERCHANT' || u.role === 'OWNER').length}
            </p>
          </div>
        </div>

        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <XCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Suspended</p>
            <p className="text-5xl font-black text-black mt-2">
              {userList.filter((u: any) => u.status === 'SUSPENDED').length}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-12 w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:border-black transition-colors pl-10"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="h-12 rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors"
          >
            <option value="ALL">All Roles</option>
            <option value="ADMIN">Admin</option>
            <option value="MERCHANT">Merchant</option>
            <option value="CUSTOMER">Customer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-12 rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      {userList.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <UsersIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-black text-black mb-2">No users found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">User</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Email</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Role</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Plan</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Stores</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Joined</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Verified</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {userList.map((user: any) => {
                  const StatusIcon = statusIcons[user.status as UserStatus] || AlertCircle;
                  const RoleIcon = roleIcons[user.role as UserRole] || UserCircle;

                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <RoleIcon className="w-4 h-4 text-black" />
                          </div>
                          <div className="min-w-0">
                            <Link
                              to={`/users/${user.id}`}
                              className="font-semibold text-black hover:text-gray-700 transition-colors text-sm block truncate"
                            >
                              {user.firstName} {user.lastName}
                            </Link>
                            <p className="text-xs text-gray-500 truncate">#{user.id.slice(0, 6)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider ${user.role === 'ADMIN' ? 'bg-black text-white' :
                          user.role === 'MERCHANT' || user.role === 'OWNER' ? 'bg-gray-800 text-white' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        {user.plan ? (
                          <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider bg-black/5 text-black border border-black/10">
                            {user.plan}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider ${user.status === 'ACTIVE' ? 'bg-black text-white' :
                          user.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                          <StatusIcon className="w-3 h-3" />
                          {user.status}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        {user._count && user._count.ownedStores > 0 ? (
                          <div className="flex items-center gap-1">
                            <ShoppingBag className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm font-semibold text-black">{user._count.ownedStores} owned</span>
                          </div>
                        ) : user.store ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <ShoppingBag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-semibold text-black truncate" title={user.store.name}>
                              {user.store.name}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">(staff)</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 whitespace-nowrap">
                            {format(new Date(user.createdAt), 'MMM d, yy')}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        {user.emailVerified ? (
                          <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider bg-black text-white">
                            <CheckCircle className="w-3 h-3" />
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-700">
                            <AlertCircle className="w-3 h-3" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/users/${user.id}`}
                            className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg border-2 border-black text-black hover:bg-black hover:text-white transition-colors"
                          >
                            View
                          </Link>
                          {user.role !== 'ADMIN' && (
                            <button
                              className={`inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${user.status === 'ACTIVE'
                                ? 'bg-gray-700 text-white hover:bg-black'
                                : 'bg-black text-white hover:bg-gray-800'
                                }`}
                              onClick={() => handleStatusToggle(user.id, user.status)}
                              disabled={updateStatusMutation.isPending}
                            >
                              {user.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

